import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router";

import { DashboardLayout } from "components/layouts";
import { EPath } from "constants/path";
import { practiceUsersApi } from "apis";
import { useCelleraTheme } from "theme/useCelleraTheme";
import { PracticeProfileDetailDto } from "libs/cellera-sdk";

const PracticeUserDetailPage: React.FC = () => {
  const { practiceUserId } = useParams<{ practiceUserId: string }>();
  const navigate = useNavigate();
  const { colors } = useCelleraTheme();

  const { data, isLoading, error, refetch } = useQuery<
    PracticeProfileDetailDto,
    Error
  >({
    queryKey: ["practice-user-detail", practiceUserId],
    queryFn: async () => {
      const res = await practiceUsersApi.getPracticeProfileByUserId({
        userId: practiceUserId || "",
      });
      return res;
    },
    enabled: Boolean(practiceUserId),
  });

  const branches = useMemo(() => data?.branches ?? [], [data]);
  const specialties = useMemo<string[]>(() => data?.specialty ?? [], [data]);

  return (
    <DashboardLayout
      title="Practice User Details"
      sideButton={
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Back
        </Button>
      }
    >
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert
            severity="error"
            action={
              <Box
                component="button"
                onClick={() => refetch()}
                style={{
                  background: "none",
                  border: "none",
                  color: colors.primary.main,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Retry
              </Box>
            }
          >
            {error.message || "Failed to load practice user"}
          </Alert>
        ) : !data ? (
          <Alert severity="info">Practice user not found.</Alert>
        ) : (
          <>
            <Paper sx={{ p: 3, border: "1px solid #E5E7EB", borderRadius: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {[data.firstName, data.middleName, data.lastName]
                      .filter(Boolean)
                      .join(" ") || "-"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {data.email || "-"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {data.phoneNumber || "-"}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Chip
                    label={data.practiceName || "Practice"}
                    size="small"
                    sx={{ backgroundColor: "#F3F4F6", color: "#374151" }}
                  />
                  <Chip
                    label={data.isActive ? "ACTIVE" : "INACTIVE"}
                    size="small"
                    color={data.isActive ? "success" : "default"}
                    variant={data.isActive ? "filled" : "outlined"}
                  />
                </Stack>
              </Stack>

              <Box
                sx={{
                  mt: 2,
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body2">{data.email || "-"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    NPI
                  </Typography>
                  <Typography variant="body2">{data.npi || "-"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body2">
                    {data.phoneNumber || "-"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Practice
                  </Typography>
                  <Typography variant="body2">
                    {data.practiceName || "-"}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Paper sx={{ p: 3, border: "1px solid #E5E7EB", borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Branches
              </Typography>
              {branches.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No branches
                </Typography>
              ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {branches.map((b) => (
                    <Chip
                      key={b.id}
                      label={
                        [b.city, b.state].filter(Boolean).join(", ") ||
                        b.address ||
                        b.id
                      }
                      size="small"
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Stack>
              )}
            </Paper>

            <Paper sx={{ p: 3, border: "1px solid #E5E7EB", borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Specialties
              </Typography>
              {specialties.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No specialties
                </Typography>
              ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {specialties.map((s: string) => (
                    <Chip key={s} label={s} size="small" sx={{ mb: 1 }} />
                  ))}
                </Stack>
              )}
            </Paper>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(EPath.PRACTICE_MANAGEMENT)}
              >
                Back to List
              </Button>
              <Button variant="contained" onClick={() => refetch()}>
                Refresh
              </Button>
            </Box>
          </>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default PracticeUserDetailPage;
